// ============================================================
// DICTIONARY SCENE
// Overlay scene for browsing the 14,400-entry toki pona dictionary
// Two scroll wheels on the left pick word A and word B,
// title shown at 2x glyph size, definition in 16px text.
// ============================================================

import Phaser from 'phaser';
import {
    GLYPH_SIZE,
    GLYPH_BUFFER,
    getGlyphColor,
    getWord,
    getEnglish,
    getCategory,
    getGlyphCount,
    getGlyphIndex,
    SPECIAL_GLYPHS,
} from '../toki/TokiPonaData';
import type { TouchControls } from '../input/TouchControls';
import { createPixelFont, PIXEL_FONT_KEY, createMicroFont, MICRO_FONT_KEY } from '../rendering/PixelFont';
import { CREATURE_SIZES, CREATURE_CATEGORY_WORD_INDEX, CREATURE_CATEGORIES } from '@blockworld/shared';
import { isAtlasFrame, atlasApplyTexture, atlasCreateImage } from '../rendering/AtlasUtils';

// Build reverse lookup: word index → creature category name (e.g., 98 → 'soweli')
const CATEGORY_BY_WORD_INDEX: Record<number, string> = {};
for (const cat of CREATURE_CATEGORIES) {
    CATEGORY_BY_WORD_INDEX[CREATURE_CATEGORY_WORD_INDEX[cat]] = cat;
}

/**
 * If the word pair (indexA, indexB) refers to a multi-tile creature,
 * return the creature ID (e.g. 'soweli_84'). Otherwise return null.
 */
export function getMultiTileCreatureId(indexA: number, indexB: number): string | null {
    const cat = CATEGORY_BY_WORD_INDEX[indexA];
    if (!cat) return null;
    const creatureId = `${cat}_${indexB}`;
    if (creatureId in CREATURE_SIZES) return creatureId;
    return null;
}

// Layout constants
const SLOT_H = 12;          // vertical spacing per glyph slot in wheel
const VISIBLE_SLOTS = 16;   // glyphs visible per wheel
const CENTER_SLOT = 7;      // which visible slot is the "selected" center
const WHEEL_TOP_Y = 16;     // top of wheel area
const WHEEL_A_X = 6;        // x position of wheel A glyphs
const WHEEL_B_X = 24;       // x position of wheel B glyphs
const WHEEL_W = 10;         // width of highlight rect
const TITLE_SIZE = 16;      // glyph draw size for buttons
const HEADER_SIZE = 32;     // 4x glyph draw size for header pair
const PADDING = 4;
const STAR_SIZE = 16;       // star rectangle size
const STAR_GAP = 4;         // gap between stars
const STAR_Y = 115;         // y position of star rating row
const STAR_COUNT = 5;
const RIGHT_START = 42;     // left edge of content area
const MUN_INDEX = 58;           // glyph index for "mun" (Stars category)

// Backdrop panel constants
const BACKDROP_TILE = 8;        // tile size in px (matches game tiles)
const BACKDROP_W = 7;           // tiles wide
const BACKDROP_H = 11;          // tiles tall
const BACKDROP_BUFFER = 8;      // px from screen edges
const BACKDROP_SKY_ROWS = 8;    // top 8 rows are sky

// Maps dictionary word pairs to Phaser texture keys (processed)
export const SPRITE_MAP: Record<string, string> = {
    'kiwen kiwen': 'stone',
    'kiwen ma': 'dirt',
    'kiwen kasi': 'grass',
    'palisa utala': 'sword',
    'pilin pilin': 'heart',
    'soweli lili': 'rodent_mouse',
    'soweli lupa': 'rodent_rat',
    'soweli akesi': 'rodent_squirrel',
    'pipi luka': 'rodent_crab',
    'kiwen ilo': 'flint',
    'kasi lipu': 'leaves',
    'palisa kasi': 'stick',
    'en palisa': 'ladder',
    'ilo noka': 'ladder',
    'pilin wawa': 'heart_container',
    'kasi insa': 'log',
    'kasi palisa': 'trunk',
    'kasi supa': 'plank',
    'kiwen lupa': 'portal',
    'kiwen sewi': 'sky_generated',
    'kiwen lawa': 'skull',
    'kiwen insa': 'amethyst',
    'kiwen kule': 'opal',
    'kiwen laso': 'turquoise',
    'kasi lili': 'sapling_generated',
    'seli mun': 'shooting_star',
    'telo telo': 'water_generated',
    'awen kasi': 'wooden_shield_generated',
    'palisa kiwen': 'stone_club_generated',
    'palisa seli': 'sword',
    'kiwen pilin': 'red_stone',
    'lupa jelo': 'yellow_door_bottom',
    'ilo jelo': 'yellow_key',
    'noka sewi': 'boots',
    // KIWEN gameplay blocks
    'kiwen anpa': 'kiwen_anpa',
    'kiwen nimi': 'kiwen_nimi',
    'kiwen seme': 'kiwen_seme',
    'kiwen lukin': 'kiwen_lukin',
    'kiwen noka': 'kiwen_noka',
    'kiwen la': 'kiwen_la',
    'kiwen supa': 'kiwen_supa',
    'kiwen toki': 'kiwen_toki',
    'kiwen mi': 'kiwen_mi',
    'kiwen soweli': 'kiwen_soweli',
    'kiwen ijo': 'kiwen_ijo',
    'kiwen jan': 'kiwen_jan',
    'kiwen jelo': 'kiwen_jelo',
    'kiwen jo': 'kiwen_jo',
    'kiwen kala': 'kiwen_kala',
    'kiwen kalama': 'kiwen_kalama',
    'kiwen kama': 'kiwen_kama',
    'kiwen ko': 'kiwen_ko',
    'kiwen kulupu': 'kiwen_kulupu',
    'kiwen kute': 'kiwen_kute',
    'kiwen len': 'kiwen_len',
    'kiwen lete': 'kiwen_lete',
    'kiwen li': 'kiwen_li',
    'kiwen lipu': 'kiwen_lipu',
    'kiwen luka': 'kiwen_luka',
    'kiwen mama': 'kiwen_mama',
    'kiwen mani': 'kiwen_mani',
    'kiwen moku': 'kiwen_moku',
    'kiwen moli': 'kiwen_moli',
    'kiwen nasin': 'kiwen_nasin',
    'kiwen ni': 'kiwen_ni',
    'kiwen o': 'kiwen_o',
    'kiwen olin': 'kiwen_olin',
    'kiwen ona': 'kiwen_ona',
    'kiwen pakala': 'kiwen_pakala',
    'kiwen pan': 'kiwen_pan',
    'kiwen pana': 'kiwen_pana',
    'kiwen pi': 'kiwen_pi',
    'kiwen pimeja': 'kiwen_pimeja',
    'kiwen pipi': 'kiwen_pipi',
    'kiwen poki': 'kiwen_poki',
    'kiwen pu': 'kiwen_pu',
    'kiwen seli': 'kiwen_seli',
    'kiwen sijelo': 'kiwen_sijelo',
    'kiwen suno': 'kiwen_suno',
    'kiwen tomo': 'kiwen_tomo',
    'kiwen unpa': 'kiwen_unpa',
    'kiwen wawa': 'kiwen_wawa',
    'kasi ale': 'kasi_ale',
};

// Maps exact definition text to textures — fallback when word pair isn't in SPRITE_MAP
export const DEFINITION_SPRITE_MAP: Record<string, string> = {
    'Mouse': 'rodent_mouse',
    'Rat': 'rodent_rat',
    'Squirrel': 'rodent_squirrel',
    'Crab': 'rodent_crab',
    'Flint': 'flint',
    'Stick': 'stick',
    'Leaves': 'leaves',
    'Ladder': 'ladder',
    'Heart Container': 'heart_container',
    'Big Heart Container': 'big_heart_container',
    'Log': 'log',
    'Trunk': 'trunk',
    'Planks': 'plank',
    'Sky': 'sky_generated',
    'Amethyst': 'amethyst',
    'Opal': 'opal',
    'Turquoise': 'turquoise',
    'Portal Block': 'portal',
    'Skull': 'skull',
    'Sapling': 'sapling_generated',
    'Shooting Star': 'shooting_star',
    'Water': 'water_generated',
    'Wooden Shield': 'wooden_shield_generated',
    'Stone Club': 'stone_club_generated',
    'Iron Sword': 'sword',
    'Yellow Door': 'yellow_door_bottom',
    'Yellow Key': 'yellow_key',
    'Boots': 'boots',
    // KIWEN gameplay blocks
    'Thwomp Stone': 'kiwen_anpa',
    'Sign Block': 'kiwen_nimi',
    'Question Block': 'kiwen_seme',
    'Eye Switch': 'kiwen_lukin',
    'Stepping Stone': 'kiwen_noka',
    'Pressure Plate': 'kiwen_la',
    'Platform Stone': 'kiwen_supa',
    'Speech Stone': 'kiwen_toki',
    'Spawn Marker': 'kiwen_mi',
    'Bone Block': 'kiwen_soweli',
    'Gray Brick Block': 'kiwen_ijo',
    'Statue Block': 'kiwen_jan',
    'Gold Block': 'kiwen_jelo',
    'Chest Block': 'kiwen_jo',
    'Prismarine Brick': 'kiwen_kala',
    'Note Block': 'kiwen_kalama',
    'Meteor Block': 'kiwen_kama',
    'Clay Block': 'kiwen_ko',
    'Castle Block': 'kiwen_kulupu',
    'Echo Stone': 'kiwen_kute',
    'Wool Block': 'kiwen_len',
    'Ice Bricks': 'kiwen_lete',
    'Command Block': 'kiwen_li',
    'Bookshelf Block': 'kiwen_lipu',
    'Piston Block': 'kiwen_luka',
    'Egg Block': 'kiwen_mama',
    'Gem Ore': 'kiwen_mani',
    'Salt Block': 'kiwen_moku',
    'Demon Altar Block': 'kiwen_moli',
    'Cobblestone Block': 'kiwen_nasin',
    'Beacon Block': 'kiwen_ni',
    '! Block': 'kiwen_o',
    'Heart Container Block': 'kiwen_olin',
    'Player Head Block': 'kiwen_ona',
    'POW Block': 'kiwen_pakala',
    'Hay Bale Block': 'kiwen_pan',
    'Dispenser Block': 'kiwen_pana',
    'Flagstone': 'kiwen_pi',
    'Blackstone Block': 'kiwen_pimeja',
    'Hive Block': 'kiwen_pipi',
    'Barrel Block': 'kiwen_poki',
    'Lectern Block': 'kiwen_pu',
    'Brimstone': 'kiwen_seli',
    'Flesh Block': 'kiwen_sijelo',
    'Glowstone Block': 'kiwen_suno',
    'Brick Block': 'kiwen_tomo',
    'Coupling Block': 'kiwen_unpa',
    'Iron Block': 'kiwen_wawa',
    'World Tree Block': 'kasi_ale',
};

/** A single note entry in the history for a dictionary slot. */
export interface NoteEntry {
    text: string;
    ts: number;
    source: 'review' | 'local';
}

export class DictionaryScene extends Phaser.Scene {
    // Last inspected tile — set by GameScene on tile click, used as default initial position
    static lastInspectedA: number | null = null;
    static lastInspectedB: number | null = null;

    // Dictionary data — loaded once, shared across scene launches
    private static dictionary: Map<string, string> | null = null;
    private static loadPromise: Promise<void> | null = null;

    // Note history — cumulative ordered notes per slot, loaded from dictionary_notes.json
    static noteHistory: Map<string, NoteEntry[]> = new Map();
    private static notesLoadPromise: Promise<void> | null = null;

    // Score data — persists in memory across scene launches
    private static scores: Map<string, { score: number }> = new Map();

    // Pasted sprite images — base64 data URLs, persists across launches
    private static pastedImages: Map<string, string> = new Map();

    // Wheel state
    private selectedA: number = 0;
    private selectedB: number = 0;
    private focusedWheel: 'A' | 'B' = 'A';

    // Wheel display
    private wheelGlyphsA: Phaser.GameObjects.Image[] = [];
    private wheelGlyphsB: Phaser.GameObjects.Image[] = [];
    private highlightA!: Phaser.GameObjects.Rectangle;
    private highlightB!: Phaser.GameObjects.Rectangle;
    private focusIndicatorA!: Phaser.GameObjects.Rectangle;
    private focusIndicatorB!: Phaser.GameObjects.Rectangle;
    private wheelFrameA!: Phaser.GameObjects.Rectangle;
    private wheelFrameB!: Phaser.GameObjects.Rectangle;

    // Title display
    private titleGlyphA!: Phaser.GameObjects.Image;
    private titleGlyphB!: Phaser.GameObjects.Image;
    private wordLabel!: Phaser.GameObjects.BitmapText;

    // English translation label
    private englishLabel!: Phaser.GameObjects.BitmapText;

    // Game object category label
    private categoryLabel!: Phaser.GameObjects.BitmapText;

    // Definition display
    private definitionText!: Phaser.GameObjects.BitmapText;

    // Keyboard
    private escKey!: Phaser.Input.Keyboard.Key;
    private upKey!: Phaser.Input.Keyboard.Key;
    private downKey!: Phaser.Input.Keyboard.Key;
    private leftKey!: Phaser.Input.Keyboard.Key;
    private rightKey!: Phaser.Input.Keyboard.Key;
    private tabKey!: Phaser.Input.Keyboard.Key;
    private spaceKey!: Phaser.Input.Keyboard.Key;
    private backspaceKey!: Phaser.Input.Keyboard.Key;

    // Touch controls
    private touchControls: TouchControls | null = null;
    private lastTouchUp: boolean = false;
    private lastTouchDown: boolean = false;
    private lastTouchLeft: boolean = false;
    private lastTouchRight: boolean = false;

    // Drag scrolling
    private dragWheel: 'A' | 'B' | null = null;
    private dragStartY: number = 0;
    private dragStartSelected: number = 0;

    // Buttons
    private escapeButton!: Phaser.GameObjects.Image;
    private nextButton!: Phaser.GameObjects.Image;
    private backButton!: Phaser.GameObjects.Image;
    private exportButton!: Phaser.GameObjects.Image;
    private pickerButtonA!: Phaser.GameObjects.Image;
    private pickerButtonB!: Phaser.GameObjects.Image;
    private glyphPickerOpen: boolean = false;
    private spriteBrowserOpen: boolean = false;

    // Star rating
    private starRects: Phaser.GameObjects.Rectangle[] = [];
    private noteLabel!: Phaser.GameObjects.BitmapText;
    private noteLines: Phaser.GameObjects.BitmapText[] = [];
    private noteScrollOffset: number = 0;
    private numKeys: Phaser.Input.Keyboard.Key[] = [];
    private nKey!: Phaser.Input.Keyboard.Key;
    private pageUpKey!: Phaser.Input.Keyboard.Key;
    private pageDownKey!: Phaser.Input.Keyboard.Key;

    // Sprite preview (on backdrop panel)
    private spritePreview!: Phaser.GameObjects.Image;
    private babySprite!: Phaser.GameObjects.Image;
    private spritePlaceholder!: Phaser.GameObjects.Rectangle;

    // DOM paste handler (must be cleaned up on shutdown)
    private pasteHandler: ((e: Event) => void) | null = null;

    // Callback
    private onClose: (() => void) | null = null;

    constructor() {
        super({ key: 'DictionaryScene' });
    }

    init(data: {
        onClose?: () => void;
        touchControls?: TouchControls;
        initialA?: number;
        initialB?: number;
    }): void {
        this.onClose = data.onClose || null;
        this.touchControls = data.touchControls || null;
        this.lastTouchUp = false;
        this.lastTouchDown = false;
        this.lastTouchLeft = false;
        this.lastTouchRight = false;
        if (data.initialA !== undefined) {
            this.selectedA = data.initialA;
        } else if (DictionaryScene.lastInspectedA !== null) {
            this.selectedA = DictionaryScene.lastInspectedA;
        }
        if (data.initialB !== undefined) {
            this.selectedB = data.initialB;
        } else if (DictionaryScene.lastInspectedB !== null) {
            this.selectedB = DictionaryScene.lastInspectedB;
        }
    }

    create(): void {
        const { width, height } = this.cameras.main;

        // Ensure pixel font is available
        createPixelFont(this);
        createMicroFont(this);

        // Reset arrays (scene is reused across stop/launch)
        this.wheelGlyphsA = [];
        this.wheelGlyphsB = [];
        this.starRects = [];

        // Semi-transparent overlay
        const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.85);
        overlay.setOrigin(0, 0).setDepth(0);

        // Keyboard
        this.escKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
        this.upKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
        this.downKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);
        this.leftKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT);
        this.rightKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT);
        this.tabKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.TAB);
        this.spaceKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
        this.backspaceKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.BACKSPACE);
        this.nKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.N);
        this.pageUpKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.PAGE_UP);
        this.pageDownKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.PAGE_DOWN);
        this.numKeys = [];
        for (let i = 1; i <= 5; i++) {
            this.numKeys.push(this.input.keyboard!.addKey(48 + i)); // KeyCodes for 1-5
        }

        // Build UI
        this.createWheels();
        this.createEscapeButton();
        this.createBackButton();
        this.createNextButton();
        this.createTitleArea();
        this.createDefinitionArea();
        this.createScoreArea();
        this.createBackdropPanel();
        this.createExportButton();
        this.createPickerButton();

        // Clipboard paste handler (Ctrl+V with image data)
        this.pasteHandler = (e: Event) => this.handlePaste(e as ClipboardEvent);
        document.addEventListener('paste', this.pasteHandler);
        this.events.on('shutdown', () => {
            if (this.pasteHandler) {
                document.removeEventListener('paste', this.pasteHandler);
                this.pasteHandler = null;
            }
        });

        // Mouse wheel scrolling
        this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: unknown[], _deltaX: number, deltaY: number) => {
            const pointer = this.input.activePointer;
            const wheel = this.getWheelAtX(pointer.x);
            if (wheel) {
                this.scrollWheel(wheel, deltaY > 0 ? 1 : -1);
            }
        });

        // Pointer drag for scroll wheels
        this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
            const wheel = this.getWheelAtX(pointer.x);
            if (wheel && pointer.y >= WHEEL_TOP_Y && pointer.y < WHEEL_TOP_Y + VISIBLE_SLOTS * SLOT_H) {
                this.dragWheel = wheel;
                this.dragStartY = pointer.y;
                this.dragStartSelected = wheel === 'A' ? this.selectedA : this.selectedB;

                // Also handle click-to-select: which slot was clicked?
                const slotIndex = Math.floor((pointer.y - WHEEL_TOP_Y) / SLOT_H);
                const scrollOffset = this.getScrollOffset(wheel);
                const clickedGlyph = scrollOffset + slotIndex;
                if (clickedGlyph >= 0 && clickedGlyph < getGlyphCount()) {
                    if (wheel === 'A') this.selectedA = clickedGlyph;
                    else this.selectedB = clickedGlyph;
                    this.focusedWheel = wheel;
                    this.updateAll();
                }
            }
        });

        this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
            if (!this.dragWheel || !pointer.isDown) return;
            const dy = this.dragStartY - pointer.y;
            const slotDelta = Math.round(dy / SLOT_H);
            if (slotDelta !== 0) {
                const newSelected = Math.max(0, Math.min(getGlyphCount() - 1, this.dragStartSelected + slotDelta));
                if (this.dragWheel === 'A' && newSelected !== this.selectedA) {
                    this.selectedA = newSelected;
                    this.updateAll();
                } else if (this.dragWheel === 'B' && newSelected !== this.selectedB) {
                    this.selectedB = newSelected;
                    this.updateAll();
                }
            }
        });

        this.input.on('pointerup', () => {
            this.dragWheel = null;
        });

        // Load dictionary data (async)
        this.loadDictionary().then(() => {
            this.updateDefinition();
            this.updateScoreDisplay();
        });

        // Initial render
        this.updateWheels();
        this.updateTitle();
        this.updateDefinition();
        this.updateScoreDisplay();
        this.updateSpritePreview();
    }

    // ============================================================
    // DICTIONARY DATA
    // ============================================================

    private async loadDictionary(): Promise<void> {
        if (DictionaryScene.dictionary) return;
        if (DictionaryScene.loadPromise) {
            await DictionaryScene.loadPromise;
            return;
        }

        DictionaryScene.loadPromise = (async () => {
            try {
                const response = await fetch('dictionary_14400.txt');
                const text = await response.text();
                DictionaryScene.dictionary = new Map();

                for (const line of text.split('\n')) {
                    const tabIndex = line.indexOf('\t');
                    if (tabIndex === -1) continue;
                    const key = line.substring(0, tabIndex).trim();
                    const value = line.substring(tabIndex + 1).trim();
                    if (key && value) {
                        DictionaryScene.dictionary.set(key, value);
                    }
                }
            } catch (e) {
                console.error('[DictionaryScene] Failed to load dictionary:', e);
            }
        })();

        await DictionaryScene.loadPromise;
        await this.loadNoteHistory();
    }

    private async loadNoteHistory(): Promise<void> {
        if (DictionaryScene.noteHistory.size > 0) return;
        if (DictionaryScene.notesLoadPromise) {
            await DictionaryScene.notesLoadPromise;
            return;
        }

        DictionaryScene.notesLoadPromise = (async () => {
            try {
                const response = await fetch('dictionary_notes.json');
                if (!response.ok) return;
                const data = await response.json() as Record<string, NoteEntry[]>;
                for (const [key, entries] of Object.entries(data)) {
                    if (Array.isArray(entries)) {
                        DictionaryScene.noteHistory.set(key, entries);
                    }
                }
            } catch (e) {
                console.error('[DictionaryScene] Failed to load note history:', e);
            }
        })();

        await DictionaryScene.notesLoadPromise;
    }

    private lookupDefinition(indexA: number, indexB: number): string {
        return DictionaryScene.lookupDef(indexA, indexB);
    }

    /** Public static lookup so other scenes can resolve definitions. */
    static lookupDef(indexA: number, indexB: number): string {
        const wordA = getWord(indexA);
        const wordB = getWord(indexB);
        if (!wordA || !wordB) return '';

        const key = `${wordA} ${wordB}`;
        return DictionaryScene.dictionary?.get(key) || '???';
    }

    // ============================================================
    // UI CREATION
    // ============================================================

    private createWheels(): void {
        const glyphCount = getGlyphCount();

        // Wheel frames (subtle border around each wheel column)
        this.wheelFrameA = this.add.rectangle(
            WHEEL_A_X - 2, WHEEL_TOP_Y - 2,
            GLYPH_SIZE + 4, VISIBLE_SLOTS * SLOT_H + 4,
            0x222222
        );
        this.wheelFrameA.setOrigin(0, 0).setDepth(1);
        this.wheelFrameA.setStrokeStyle(1, 0x444444);

        this.wheelFrameB = this.add.rectangle(
            WHEEL_B_X - 2, WHEEL_TOP_Y - 2,
            GLYPH_SIZE + 4, VISIBLE_SLOTS * SLOT_H + 4,
            0x222222
        );
        this.wheelFrameB.setOrigin(0, 0).setDepth(1);
        this.wheelFrameB.setStrokeStyle(1, 0x444444);

        // Selection highlights (at the center slot of each wheel)
        const centerY = WHEEL_TOP_Y + CENTER_SLOT * SLOT_H - 1;
        this.highlightA = this.add.rectangle(
            WHEEL_A_X - 1, centerY,
            WHEEL_W, SLOT_H,
            0xffffff, 0.2
        );
        this.highlightA.setOrigin(0, 0).setDepth(2);

        this.highlightB = this.add.rectangle(
            WHEEL_B_X - 1, centerY,
            WHEEL_W, SLOT_H,
            0xffffff, 0.2
        );
        this.highlightB.setOrigin(0, 0).setDepth(2);

        // Focus indicators (brighter border on focused wheel)
        this.focusIndicatorA = this.add.rectangle(
            WHEEL_A_X - 2, centerY - 1,
            WHEEL_W + 2, SLOT_H + 2
        );
        this.focusIndicatorA.setOrigin(0, 0).setDepth(2);
        this.focusIndicatorA.setFillStyle(0x000000, 0);
        this.focusIndicatorA.setStrokeStyle(1, 0xffffff, 0.6);

        this.focusIndicatorB = this.add.rectangle(
            WHEEL_B_X - 2, centerY - 1,
            WHEEL_W + 2, SLOT_H + 2
        );
        this.focusIndicatorB.setOrigin(0, 0).setDepth(2);
        this.focusIndicatorB.setFillStyle(0x000000, 0);
        this.focusIndicatorB.setStrokeStyle(1, 0xffffff, 0.6);

        // Create glyph images for each visible slot in both wheels
        for (let i = 0; i < VISIBLE_SLOTS; i++) {
            const y = WHEEL_TOP_Y + i * SLOT_H;

            // Wheel A glyph
            const glyphA = this.add.image(WHEEL_A_X, y, 'toki_glyphs', 0);
            glyphA.setOrigin(0, 0).setDepth(3);
            this.wheelGlyphsA.push(glyphA);

            // Wheel B glyph
            const glyphB = this.add.image(WHEEL_B_X, y, 'toki_glyphs', 0);
            glyphB.setOrigin(0, 0).setDepth(3);
            this.wheelGlyphsB.push(glyphB);
        }
    }

    private createEscapeButton(): void {
        const { width } = this.cameras.main;
        const alaIndex = getGlyphIndex(SPECIAL_GLYPHS.ESCAPE);
        this.escapeButton = this.add.image(width - PADDING - TITLE_SIZE, PADDING, 'toki_glyphs', alaIndex);
        this.escapeButton.setOrigin(0, 0);
        this.escapeButton.setDisplaySize(TITLE_SIZE, TITLE_SIZE);
        this.escapeButton.setDepth(10);
        this.escapeButton.setTint(getGlyphColor(alaIndex));
        this.escapeButton.setInteractive({ useHandCursor: true });
        this.escapeButton.on('pointerdown', () => this.closeScene());
    }

    private createBackButton(): void {
        const { width, height } = this.cameras.main;
        const monsiIndex = getGlyphIndex('monsi');
        const gap = 4;
        const navSize = TITLE_SIZE * 1.5; // 50% bigger than standard buttons
        const btnX = Math.floor(width / 2) - navSize - Math.floor(gap / 2);
        const btnY = height - PADDING - navSize;
        this.backButton = this.add.image(btnX, btnY, 'toki_glyphs', monsiIndex);
        this.backButton.setOrigin(0, 0);
        this.backButton.setDisplaySize(navSize, navSize);
        this.backButton.setDepth(10);
        this.backButton.setTint(getGlyphColor(monsiIndex));
        this.backButton.setInteractive({ useHandCursor: true });
        this.backButton.on('pointerdown', () => this.advanceBack());
    }

    private createNextButton(): void {
        const { width, height } = this.cameras.main;
        const tawaIndex = getGlyphIndex('tawa');
        const gap = 4;
        const navSize = TITLE_SIZE * 1.5; // 50% bigger than standard buttons
        const btnX = Math.floor(width / 2) + Math.floor(gap / 2);
        const btnY = height - PADDING - navSize;
        this.nextButton = this.add.image(btnX, btnY, 'toki_glyphs', tawaIndex);
        this.nextButton.setOrigin(0, 0);
        this.nextButton.setDisplaySize(navSize, navSize);
        this.nextButton.setDepth(10);
        this.nextButton.setTint(getGlyphColor(tawaIndex));
        this.nextButton.setInteractive({ useHandCursor: true });
        this.nextButton.on('pointerdown', () => this.advanceNext());
    }

    private advanceBack(): void {
        const max = getGlyphCount() - 1;
        if (this.selectedB > 0) {
            this.selectedB--;
        } else if (this.selectedA > 0) {
            this.selectedB = max;
            this.selectedA--;
        }
        this.updateAll();
    }

    private advanceNext(): void {
        const max = getGlyphCount() - 1;
        if (this.selectedB < max) {
            this.selectedB++;
        } else if (this.selectedA < max) {
            this.selectedB = 0;
            this.selectedA++;
        }
        this.updateAll();
    }

    private updateAll(): void {
        this.noteScrollOffset = 0;
        this.updateWheels();
        this.updateTitle();
        this.updateDefinition();
        this.updateScoreDisplay();
        this.updateSpritePreview();
    }

    private createTitleArea(): void {
        const { width } = this.cameras.main;

        // Right-side content area
        const rightStart = 42;
        const rightCenter = rightStart + (width - rightStart) / 2;

        // Game object category label at top-left of content area
        this.categoryLabel = this.add.bitmapText(rightStart, PADDING, PIXEL_FONT_KEY, '');
        this.categoryLabel.setOrigin(0, 0).setDepth(5);
        this.categoryLabel.setTint(0x666666);

        // Title glyphs at 4x size (32x32), centered in right area
        const titleY = PADDING;
        const gap = 4;
        const totalTitleW = HEADER_SIZE * 2 + gap;
        const titleStartX = rightCenter - totalTitleW / 2;

        this.titleGlyphA = this.add.image(titleStartX, titleY, 'toki_glyphs', this.selectedA);
        this.titleGlyphA.setOrigin(0, 0);
        this.titleGlyphA.setDisplaySize(HEADER_SIZE, HEADER_SIZE);
        this.titleGlyphA.setDepth(5);
        this.titleGlyphA.setTint(getGlyphColor(this.selectedA));

        this.titleGlyphB = this.add.image(titleStartX + HEADER_SIZE + gap, titleY, 'toki_glyphs', this.selectedB);
        this.titleGlyphB.setOrigin(0, 0);
        this.titleGlyphB.setDisplaySize(HEADER_SIZE, HEADER_SIZE);
        this.titleGlyphB.setDepth(5);
        this.titleGlyphB.setTint(getGlyphColor(this.selectedB));

        // Word name label below title (toki pona)
        this.wordLabel = this.add.bitmapText(rightCenter, titleY + HEADER_SIZE + 2, PIXEL_FONT_KEY, '');
        this.wordLabel.setOrigin(0.5, 0).setDepth(5);
        this.wordLabel.setTint(0xaaaaaa);

        // English translation label below toki pona
        this.englishLabel = this.add.bitmapText(rightCenter, titleY + HEADER_SIZE + 14, PIXEL_FONT_KEY, '');
        this.englishLabel.setOrigin(0.5, 0).setDepth(5);
        this.englishLabel.setTint(0x888888);
    }

    private createDefinitionArea(): void {
        const rightStart = 42;
        const defY = 78;

        this.definitionText = this.add.bitmapText(rightStart, defY, PIXEL_FONT_KEY, '');
        this.definitionText.setOrigin(0, 0).setDepth(5);
        this.definitionText.setMaxWidth(this.cameras.main.width - rightStart - PADDING);
    }

    private createScoreArea(): void {
        this.starRects = [];

        // Stars on the left half of the content area
        for (let i = 0; i < STAR_COUNT; i++) {
            const x = RIGHT_START + i * (STAR_SIZE + STAR_GAP);
            const star = this.add.rectangle(x, STAR_Y, STAR_SIZE, STAR_SIZE, 0x333333);
            star.setOrigin(0, 0).setDepth(5);
            star.setStrokeStyle(1, 0x555555);
            star.setInteractive({ useHandCursor: true });
            const rating = i + 1;
            star.on('pointerdown', () => this.setScore(rating));
            this.starRects.push(star);
        }

        // Note label (click to edit) — left half, below stars
        const halfWidth = (this.cameras.main.width - RIGHT_START) / 2;
        const noteX = RIGHT_START - 6;
        this.noteLabel = this.add.bitmapText(noteX, STAR_Y + STAR_SIZE + 4, MICRO_FONT_KEY, '');
        this.noteLabel.setOrigin(0, 0).setDepth(5);
        this.noteLabel.setTint(0x777777);
        this.noteLabel.setMaxWidth(Math.floor((halfWidth - PADDING) * 4 / 3));
        this.noteLabel.setInteractive({ useHandCursor: true });
        this.noteLabel.on('pointerdown', () => this.editNote());
    }

    private createBackdropPanel(): void {
        const { width, height } = this.cameras.main;
        const panelX = width - BACKDROP_BUFFER - BACKDROP_W * BACKDROP_TILE;
        const panelY = height - BACKDROP_BUFFER - BACKDROP_H * BACKDROP_TILE;

        // Build backdrop: sky (8 rows), grass (1 row), dirt (1 row), stone (1 row)
        for (let row = 0; row < BACKDROP_H; row++) {
            for (let col = 0; col < BACKDROP_W; col++) {
                const x = panelX + col * BACKDROP_TILE;
                const y = panelY + row * BACKDROP_TILE;

                let textureKey: string;
                if (row < BACKDROP_SKY_ROWS) {
                    textureKey = 'sky_generated';
                } else if (row === BACKDROP_SKY_ROWS) {
                    textureKey = 'grass';
                } else if (row === BACKDROP_SKY_ROWS + 1) {
                    textureKey = 'dirt';
                } else {
                    textureKey = 'stone';
                }

                const tile = atlasCreateImage(this, x, y, textureKey);
                tile.setOrigin(0, 0);
                tile.setDepth(4);
            }
        }

        // Object sprite sits at bottom-center of sky section (on top of grass)
        const centerX = panelX + (BACKDROP_W * BACKDROP_TILE) / 2;
        const grassTopY = panelY + BACKDROP_SKY_ROWS * BACKDROP_TILE;

        // Magenta placeholder (shown when no sprite exists)
        this.spritePlaceholder = this.add.rectangle(
            centerX, grassTopY,
            BACKDROP_TILE, BACKDROP_TILE, 0xff00ff
        );
        this.spritePlaceholder.setOrigin(0.5, 1);
        this.spritePlaceholder.setDepth(5);

        // Sprite preview image (hidden initially, positioned on backdrop)
        this.spritePreview = this.add.image(centerX, grassTopY, '__DEFAULT');
        this.spritePreview.setOrigin(0.5, 1);
        this.spritePreview.setDepth(5);
        this.spritePreview.setVisible(false);

        // Baby sprite (shown to the left of full sprite for multi-tile creatures)
        this.babySprite = this.add.image(centerX, grassTopY, '__DEFAULT');
        this.babySprite.setOrigin(0.5, 1);
        this.babySprite.setDepth(5);
        this.babySprite.setVisible(false);
    }

    private createExportButton(): void {
        const { width } = this.cameras.main;
        const panaIndex = getGlyphIndex('pana');
        this.exportButton = this.add.image(width - PADDING - TITLE_SIZE * 2 - PADDING, PADDING, 'toki_glyphs', panaIndex);
        this.exportButton.setOrigin(0, 0);
        this.exportButton.setDisplaySize(TITLE_SIZE, TITLE_SIZE);
        this.exportButton.setDepth(10);
        this.exportButton.setTint(getGlyphColor(panaIndex));
        this.exportButton.setInteractive({ useHandCursor: true });
        this.exportButton.on('pointerdown', () => this.exportScores());
    }

    private createPickerButton(): void {
        // "ale" (all) glyph above wheel A — opens the 120-glyph quick-select
        const aleIndex = getGlyphIndex('ale');
        this.pickerButtonA = this.add.image(WHEEL_A_X, PADDING, 'toki_glyphs', aleIndex);
        this.pickerButtonA.setOrigin(0, 0);
        this.pickerButtonA.setDepth(10);
        this.pickerButtonA.setTint(getGlyphColor(aleIndex));
        this.pickerButtonA.setInteractive({ useHandCursor: true });
        this.pickerButtonA.on('pointerdown', () => this.openGlyphPicker());

        // "ale" glyph above wheel B — opens the sprite browser for the selected category
        this.pickerButtonB = this.add.image(WHEEL_B_X, PADDING, 'toki_glyphs', aleIndex);
        this.pickerButtonB.setOrigin(0, 0);
        this.pickerButtonB.setDepth(10);
        this.pickerButtonB.setTint(getGlyphColor(aleIndex));
        this.pickerButtonB.setInteractive({ useHandCursor: true });
        this.pickerButtonB.on('pointerdown', () => this.openSpriteBrowser());
    }

    private openGlyphPicker(): void {
        if (this.glyphPickerOpen || this.spriteBrowserOpen) return;
        this.glyphPickerOpen = true;
        this.scene.launch('GlyphPickerScene', {
            mobile: this.touchControls?.isEnabled() ?? false,
            onSelect: (index: number) => {
                this.glyphPickerOpen = false;
                this.selectedA = index;
                this.updateAll();
            },
            onClose: () => {
                this.glyphPickerOpen = false;
            },
        });
    }

    private openSpriteBrowser(): void {
        if (this.spriteBrowserOpen || this.glyphPickerOpen) return;
        this.spriteBrowserOpen = true;
        this.scene.launch('SpriteBrowserScene', {
            wordA: this.selectedA,
            mobile: this.touchControls?.isEnabled() ?? false,
            onSelect: (index: number) => {
                this.spriteBrowserOpen = false;
                this.selectedB = index;
                this.updateAll();
            },
            onClose: () => {
                this.spriteBrowserOpen = false;
            },
        });
    }

    // ============================================================
    // SCORING
    // ============================================================

    private getScoreKey(): string {
        const wordA = getWord(this.selectedA) || '?';
        const wordB = getWord(this.selectedB) || '?';
        return `${wordA} ${wordB}`;
    }

    private setScore(rating: number): void {
        const key = this.getScoreKey();
        DictionaryScene.scores.set(key, { score: rating });
        this.updateScoreDisplay();
    }

    private editNote(): void {
        const key = this.getScoreKey();
        const result = window.prompt('Add note for ' + key + ':');
        if (result !== null && result.trim() !== '') {
            const entry: NoteEntry = {
                text: result.trim(),
                ts: Date.now(),
                source: 'local',
            };
            const history = DictionaryScene.noteHistory.get(key) || [];
            history.push(entry);
            DictionaryScene.noteHistory.set(key, history);
            this.noteScrollOffset = 0;
            this.updateScoreDisplay();
        }
    }

    private handlePaste(e: ClipboardEvent): void {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item.type.startsWith('image/')) continue;

            const blob = item.getAsFile();
            if (!blob) continue;

            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result as string;
                const img = new Image();
                img.onload = () => {
                    if (img.width % 8 !== 0 || img.height % 8 !== 0) {
                        console.warn(`[DictionaryScene] Pasted image ${img.width}x${img.height} — dimensions must be multiples of 8`);
                        return;
                    }

                    const key = this.getScoreKey();
                    DictionaryScene.pastedImages.set(key, dataUrl);

                    // Create Phaser texture for preview
                    const texKey = `pasted_${key.replace(/ /g, '_')}`;
                    if (this.textures.exists(texKey)) {
                        this.textures.remove(texKey);
                    }
                    this.textures.addImage(texKey, img);

                    this.updateSpritePreview();
                };
                img.src = dataUrl;
            };
            reader.readAsDataURL(blob);
            break;
        }
    }

    private updateScoreDisplay(): void {
        const key = this.getScoreKey();
        const scoreEntry = DictionaryScene.scores.get(key);
        const score = scoreEntry?.score || 0;

        for (let i = 0; i < STAR_COUNT; i++) {
            this.starRects[i].setFillStyle(i < score ? 0xffcc00 : 0x333333);
        }

        // Note history display
        const history = DictionaryScene.noteHistory.get(key) || [];
        // Destroy previous note line objects
        for (const line of this.noteLines) line.destroy();
        this.noteLines = [];

        if (history.length === 0) {
            this.noteLabel.setText('[N: add note]');
            this.noteLabel.setTint(0x555555);
            return;
        }

        // Show header with count
        this.noteLabel.setText(`notes (${history.length})  [N: add]`);
        this.noteLabel.setTint(0x777777);

        // Display notes newest-first, scrollable
        const MAX_VISIBLE_NOTES = 6;
        const NOTE_LINE_H = 7;
        const noteX = RIGHT_START - 6;
        const noteStartY = STAR_Y + STAR_SIZE + 12;
        const halfWidth = (this.cameras.main.width - RIGHT_START) / 2;
        const maxW = Math.floor((halfWidth - PADDING) * 4 / 3);

        const reversed = [...history].reverse();
        const clamped = Math.max(0, Math.min(this.noteScrollOffset, reversed.length - MAX_VISIBLE_NOTES));
        this.noteScrollOffset = clamped;
        const visible = reversed.slice(clamped, clamped + MAX_VISIBLE_NOTES);

        for (let i = 0; i < visible.length; i++) {
            const entry = visible[i];
            const age = this.formatAge(entry.ts);
            const prefix = entry.source === 'review' ? '\x80' : '\x81'; // bullet chars
            const lineText = `${age} ${entry.text}`;
            const line = this.add.bitmapText(noteX, noteStartY + i * NOTE_LINE_H, MICRO_FONT_KEY, lineText);
            line.setOrigin(0, 0).setDepth(5);
            line.setMaxWidth(maxW);
            line.setTint(entry.source === 'review' ? 0x88aacc : 0xccaa88);
            this.noteLines.push(line);
        }

        // Scroll indicators
        if (clamped > 0 || clamped + MAX_VISIBLE_NOTES < reversed.length) {
            const indicator = clamped > 0 && clamped + MAX_VISIBLE_NOTES < reversed.length
                ? '..more above & below..'
                : clamped > 0 ? '..more above..' : '..more below..';
            const ind = this.add.bitmapText(noteX, noteStartY + MAX_VISIBLE_NOTES * NOTE_LINE_H, MICRO_FONT_KEY, indicator);
            ind.setOrigin(0, 0).setDepth(5).setTint(0x555555);
            this.noteLines.push(ind);
        }
    }

    /** Format a timestamp as a short relative age string. */
    private formatAge(ts: number): string {
        const diffMs = Date.now() - ts;
        const days = Math.floor(diffMs / 86400000);
        if (days > 30) return `${Math.floor(days / 30)}mo`;
        if (days > 0) return `${days}d`;
        const hours = Math.floor(diffMs / 3600000);
        if (hours > 0) return `${hours}h`;
        return 'now';
    }

    private exportScores(): void {
        // Collect keys with new local notes
        const keysWithLocalNotes = new Set<string>();
        DictionaryScene.noteHistory.forEach((entries, key) => {
            if (entries.some(e => e.source === 'local')) keysWithLocalNotes.add(key);
        });

        if (DictionaryScene.scores.size === 0 && DictionaryScene.pastedImages.size === 0 && keysWithLocalNotes.size === 0) {
            window.alert('No scores, sprites, or notes to export.');
            return;
        }

        // Collect all keys that have scores, pasted images, or new notes
        const allKeys = new Set<string>();
        DictionaryScene.scores.forEach((_, key) => allKeys.add(key));
        DictionaryScene.pastedImages.forEach((_, key) => allKeys.add(key));
        keysWithLocalNotes.forEach(key => allKeys.add(key));

        const entries: object[] = [];
        allKeys.forEach((key) => {
            const [wordA] = key.split(' ');
            const idxA = getGlyphIndex(wordA);
            const scoreData = DictionaryScene.scores.get(key);
            const image = DictionaryScene.pastedImages.get(key);
            // Include only new local notes (not ones already from review manifests)
            const history = DictionaryScene.noteHistory.get(key) || [];
            const localNotes = history.filter(e => e.source === 'local');
            const latestNote = localNotes.length > 0 ? localNotes[localNotes.length - 1].text : '';
            entries.push({
                key,
                category: getCategory(idxA) || '?',
                definition: DictionaryScene.dictionary?.get(key) || '???',
                score: scoreData?.score || 0,
                note: latestNote,
                noteHistory: localNotes.length > 0 ? localNotes : undefined,
                ...(image ? { image } : {}),
            });
        });

        // Derive HTTP URL from WebSocket URL
        const wsUrl = import.meta.env.VITE_SERVER_URL || 'ws://localhost:2567';
        const httpUrl = wsUrl.replace(/^ws/, 'http');

        fetch(`${httpUrl}/api/dictionary-review`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ entries }),
        }).then(async (res) => {
            if (res.ok) {
                const data = await res.json();
                const sprites = data.failedSprites as string[] | undefined;
                const instructions = `Review #${data.issue} posted: check the sprites review folder manifest and make all of the changes, especially dictionary name changes and sprite changes. Move sprites from review to production. Change dictionary definition in the 14400 file.`;
                if (sprites && sprites.length > 0) {
                    window.alert(`${instructions}\n\nSprite upload failed for: ${sprites.join(', ')}\nCheck that the GitHub token has contents:write permission.`);
                } else {
                    window.alert(instructions);
                }
                // Mark exported local notes as review notes (they're now in a manifest)
                DictionaryScene.noteHistory.forEach((entries) => {
                    for (const entry of entries) {
                        if (entry.source === 'local') entry.source = 'review';
                    }
                });
                DictionaryScene.scores.clear();
                DictionaryScene.pastedImages.clear();
                this.updateScoreDisplay();
                this.updateSpritePreview();
            } else {
                const err = await res.json().catch(() => ({ error: 'Unknown error' }));
                window.alert(`Submit failed: ${err.error || res.status}`);
            }
        }).catch(() => {
            window.alert('Could not reach server.');
        });
    }

    // ============================================================
    // WHEEL HELPERS
    // ============================================================

    private getScrollOffset(wheel: 'A' | 'B'): number {
        const selected = wheel === 'A' ? this.selectedA : this.selectedB;
        // Center the selected item in the visible slots
        return selected - CENTER_SLOT;
    }

    private getWheelAtX(x: number): 'A' | 'B' | null {
        if (x >= WHEEL_A_X - 2 && x < WHEEL_A_X + GLYPH_SIZE + 2) return 'A';
        if (x >= WHEEL_B_X - 2 && x < WHEEL_B_X + GLYPH_SIZE + 2) return 'B';
        return null;
    }

    private scrollWheel(wheel: 'A' | 'B', delta: number): void {
        const glyphCount = getGlyphCount();
        if (wheel === 'A') {
            this.selectedA = Math.max(0, Math.min(glyphCount - 1, this.selectedA + delta));
        } else {
            this.selectedB = Math.max(0, Math.min(glyphCount - 1, this.selectedB + delta));
        }
        this.focusedWheel = wheel;
        this.updateAll();
    }

    // ============================================================
    // RENDER UPDATES
    // ============================================================

    private updateWheels(): void {
        const glyphCount = getGlyphCount();

        // Update wheel A
        const offsetA = this.getScrollOffset('A');
        for (let i = 0; i < VISIBLE_SLOTS; i++) {
            const glyphIndex = offsetA + i;
            const glyph = this.wheelGlyphsA[i];
            if (glyphIndex >= 0 && glyphIndex < glyphCount) {
                glyph.setFrame(glyphIndex);
                glyph.setTint(glyphIndex === this.selectedA ? 0xffffff : getGlyphColor(glyphIndex));
                // Alpha fade for wheel effect
                const dist = Math.abs(i - CENTER_SLOT);
                glyph.setAlpha(Math.max(0.2, 1.0 - dist * 0.1));
                glyph.setVisible(true);
            } else {
                glyph.setVisible(false);
            }
        }

        // Update wheel B
        const offsetB = this.getScrollOffset('B');
        for (let i = 0; i < VISIBLE_SLOTS; i++) {
            const glyphIndex = offsetB + i;
            const glyph = this.wheelGlyphsB[i];
            if (glyphIndex >= 0 && glyphIndex < glyphCount) {
                glyph.setFrame(glyphIndex);
                glyph.setTint(glyphIndex === this.selectedB ? 0xffffff : getGlyphColor(glyphIndex));
                const dist = Math.abs(i - CENTER_SLOT);
                glyph.setAlpha(Math.max(0.2, 1.0 - dist * 0.1));
                glyph.setVisible(true);
            } else {
                glyph.setVisible(false);
            }
        }

        // Focus indicators
        this.focusIndicatorA.setStrokeStyle(1, 0xffffff, this.focusedWheel === 'A' ? 0.8 : 0.2);
        this.focusIndicatorB.setStrokeStyle(1, 0xffffff, this.focusedWheel === 'B' ? 0.8 : 0.2);
    }

    private updateTitle(): void {
        // Update title glyphs
        this.titleGlyphA.setFrame(this.selectedA);
        this.titleGlyphA.setTint(getGlyphColor(this.selectedA));

        this.titleGlyphB.setFrame(this.selectedB);
        this.titleGlyphB.setTint(getGlyphColor(this.selectedB));

        // Update word label (toki pona)
        const wordA = getWord(this.selectedA) || '?';
        const wordB = getWord(this.selectedB) || '?';
        this.wordLabel.setText(`${wordA} ${wordB}`);

        // Update English label
        const engA = getEnglish(this.selectedA) || '?';
        const engB = getEnglish(this.selectedB) || '?';
        this.englishLabel.setText(`${engA} ${engB}`);

        // Update category label (first word only)
        const catA = getCategory(this.selectedA) || '?';
        this.categoryLabel.setText(catA);
    }

    private updateDefinition(): void {
        const def = this.lookupDefinition(this.selectedA, this.selectedB);
        this.definitionText.setText(def);
    }

    private updateSpritePreview(): void {
        const key = this.getScoreKey();
        const pastedTexKey = `pasted_${key.replace(/ /g, '_')}`;

        // Reset baby sprite every update
        this.babySprite.setVisible(false);

        // Pasted images take priority
        if (DictionaryScene.pastedImages.has(key) && this.textures.exists(pastedTexKey)) {
            atlasApplyTexture(this, this.spritePreview, pastedTexKey);
            this.spritePreview.setScale(1);
            this.spritePreview.setVisible(true);
            this.spritePlaceholder.setVisible(false);
            this.repositionSprites(false);
            return;
        }

        // Starmap constellation for "mun" category entries
        if (this.selectedA === MUN_INDEX && this.textures.exists('starmap')) {
            this.spritePreview.setTexture('starmap', this.selectedB);
            this.spritePreview.setScale(1);
            this.spritePreview.setVisible(true);
            this.spritePlaceholder.setVisible(false);
            this.repositionSprites(false);
            return;
        }

        // Fall back to built-in sprite map (word pair), then definition-based map,
        // then try dynamic tile_*/creature_* texture keys matching GameScene preload naming
        const texExists = (k: string) => this.textures.exists(k) || isAtlasFrame(this, k);
        let textureKey = SPRITE_MAP[key];
        if (!textureKey || !texExists(textureKey)) {
            const def = this.lookupDefinition(this.selectedA, this.selectedB);
            textureKey = DEFINITION_SPRITE_MAP[def] || '';
        }
        if (!textureKey || !texExists(textureKey)) {
            const underscored = key.replace(/ /g, '_');
            const tileKey = `tile_${underscored}`;
            const creatureKey = `creature_${underscored}`;
            if (this.textures.exists(tileKey)) {
                textureKey = this.ensureTransparent(tileKey);
            } else if (this.textures.exists(creatureKey)) {
                textureKey = this.ensureTransparent(creatureKey);
            } else if (isAtlasFrame(this, underscored)) {
                textureKey = underscored;
            }
        }
        if (textureKey && (this.textures.exists(textureKey) || isAtlasFrame(this, textureKey))) {
            atlasApplyTexture(this, this.spritePreview, textureKey);
            // Scale multi-size sprites to fit preview area (max 8 tiles wide/tall)
            const frame = isAtlasFrame(this, textureKey)
                ? this.textures.get('game_atlas').get(textureKey)
                : this.textures.get(textureKey).get();
            const maxPreviewPx = 8 * 8; // 8 tiles × 8px
            const scaleX = frame.width > maxPreviewPx ? maxPreviewPx / frame.width : 1;
            const scaleY = frame.height > maxPreviewPx ? maxPreviewPx / frame.height : 1;
            const scale = Math.min(scaleX, scaleY);
            this.spritePreview.setScale(scale);
            this.spritePreview.setVisible(true);
            this.spritePlaceholder.setVisible(false);

            // For multi-tile creatures, show baby sprite to the left
            const multiId = getMultiTileCreatureId(this.selectedA, this.selectedB);
            if (multiId) {
                const babyTexKey = `rodent_${multiId}_baby`;
                if (this.textures.exists(babyTexKey) || isAtlasFrame(this, babyTexKey)) {
                    atlasApplyTexture(this, this.babySprite, babyTexKey);
                    this.babySprite.setScale(1);
                    this.babySprite.setVisible(true);
                    this.repositionSprites(true);
                    return;
                }
            }
            this.repositionSprites(false);
        } else {
            // No sprite — show magenta placeholder on backdrop
            this.spritePreview.setVisible(false);
            this.spritePlaceholder.setVisible(true);
            this.repositionSprites(false);
        }
    }

    /** Reposition sprites on the backdrop panel, optionally making room for baby sprite. */
    private repositionSprites(showBaby: boolean): void {
        const { width, height } = this.cameras.main;
        const panelX = width - BACKDROP_BUFFER - BACKDROP_W * BACKDROP_TILE;
        const centerX = panelX + (BACKDROP_W * BACKDROP_TILE) / 2;
        const grassTopY = height - BACKDROP_BUFFER - BACKDROP_H * BACKDROP_TILE
            + BACKDROP_SKY_ROWS * BACKDROP_TILE;

        if (showBaby && this.spritePreview.visible) {
            // Baby sprite (8x8, 1 tile) to the left, full sprite to the right
            // Compute the full sprite's scaled width
            const tex = this.textures.get(this.spritePreview.texture.key);
            const frame = tex.get();
            const fullW = frame.width * this.spritePreview.scaleX;
            const babyW = BACKDROP_TILE; // 8px
            const gap = 2;
            const totalW = babyW + gap + fullW;
            const startX = centerX - totalW / 2;

            // Baby: left side, origin bottom-center
            this.babySprite.setPosition(startX + babyW / 2, grassTopY);

            // Full sprite: right side, origin bottom-center
            this.spritePreview.setPosition(startX + babyW + gap + fullW / 2, grassTopY);
        } else {
            // Normal: center the sprite
            this.spritePreview.setPosition(centerX, grassTopY);
            this.babySprite.setPosition(centerX, grassTopY);
        }
    }

    /** Make black pixels transparent for a raw texture, caching the result. */
    private ensureTransparent(rawKey: string): string {
        const processedKey = rawKey + '_dict_processed';
        if (this.textures.exists(processedKey)) return processedKey;

        const texture = this.textures.get(rawKey);
        if (!texture) return rawKey;
        const source = texture.getSourceImage() as HTMLImageElement;

        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = source.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(source, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const threshold = 10;
        for (let i = 0; i < data.length; i += 4) {
            if (data[i] <= threshold && data[i + 1] <= threshold && data[i + 2] <= threshold) {
                data[i + 3] = 0;
            }
        }
        ctx.putImageData(imageData, 0, 0);
        this.textures.addCanvas(processedKey, canvas);
        return processedKey;
    }

    // ============================================================
    // SCENE LIFECYCLE
    // ============================================================

    private closeScene(): void {
        this.onClose?.();
        this.scene.stop();
    }

    update(): void {
        // Keyboard: ESC to close
        if (Phaser.Input.Keyboard.JustDown(this.escKey)) {
            this.closeScene();
            return;
        }

        // Keyboard: arrow keys to navigate
        if (Phaser.Input.Keyboard.JustDown(this.upKey)) {
            this.scrollWheel(this.focusedWheel, -1);
        }
        if (Phaser.Input.Keyboard.JustDown(this.downKey)) {
            this.scrollWheel(this.focusedWheel, 1);
        }
        if (Phaser.Input.Keyboard.JustDown(this.leftKey)) {
            this.focusedWheel = 'A';
            this.updateWheels();
        }
        if (Phaser.Input.Keyboard.JustDown(this.rightKey)) {
            this.focusedWheel = 'B';
            this.updateWheels();
        }
        if (Phaser.Input.Keyboard.JustDown(this.tabKey)) {
            this.focusedWheel = this.focusedWheel === 'A' ? 'B' : 'A';
            this.updateWheels();
        }
        if (Phaser.Input.Keyboard.JustDown(this.spaceKey)) {
            this.advanceNext();
        }
        if (Phaser.Input.Keyboard.JustDown(this.backspaceKey)) {
            this.advanceBack();
        }

        // Number keys 1-5 for star rating
        for (let i = 0; i < this.numKeys.length; i++) {
            if (Phaser.Input.Keyboard.JustDown(this.numKeys[i])) {
                this.setScore(i + 1);
            }
        }
        // N key for note editing
        if (Phaser.Input.Keyboard.JustDown(this.nKey)) {
            this.editNote();
        }
        // Page Up/Down to scroll note history
        if (Phaser.Input.Keyboard.JustDown(this.pageUpKey)) {
            if (this.noteScrollOffset > 0) {
                this.noteScrollOffset--;
                this.updateScoreDisplay();
            }
        }
        if (Phaser.Input.Keyboard.JustDown(this.pageDownKey)) {
            this.noteScrollOffset++;
            this.updateScoreDisplay();
        }

        // Mobile touch controls
        if (this.touchControls) {
            const touchUp = this.touchControls.state.up;
            const touchDown = this.touchControls.state.down;
            const touchLeft = this.touchControls.state.left;
            const touchRight = this.touchControls.state.right;

            if (touchUp && !this.lastTouchUp) {
                this.scrollWheel(this.focusedWheel, -1);
            }
            if (touchDown && !this.lastTouchDown) {
                this.scrollWheel(this.focusedWheel, 1);
            }
            if (touchLeft && !this.lastTouchLeft) {
                this.focusedWheel = 'A';
                this.updateWheels();
            }
            if (touchRight && !this.lastTouchRight) {
                this.focusedWheel = 'B';
                this.updateWheels();
            }

            this.lastTouchUp = touchUp;
            this.lastTouchDown = touchDown;
            this.lastTouchLeft = touchLeft;
            this.lastTouchRight = touchRight;
        }
    }
}
